// apps/web/src/components/ai/ai-chat-panel.jsx
// Slide-in Chat-Panel für den globalen KI-Assistenten.

import { useState, useRef, useEffect, useMemo, useReducer } from "react";
import { useNavigate } from "react-router-dom";
import { navigateTo } from "@tennis-matches/core/router";
import { buildTrainingsDeskPath } from "@tennis-matches/core";
import { useAI } from "./ai-context.jsx";
import { createToolRunner } from "../../ai/toolRunner.js";
import {
  loadMessages as loadStoredMessages,
  saveMessages as saveStoredMessages,
  loadFlowState,
  saveFlowState,
} from "./flows/flow.storage.js";
import {
  getInitialState,
  startFlow,
  cancelFlow,
  handleUserMessage,
} from "./flows/flow.engine.js";
import {
  detectIntent,
  routeIntent,
  detectOpenAppRoute,
} from "./flows/flow.intent.js";
import {
  getFlowIdForArea,
  TOURNAMENT_AREAS,
  AREA_SUGGESTION_ITEMS,
  AREA_UI,
  SCOPE_OFF_TOPIC_QUICK_REPLIES,
  getEmptyStateSuggestions,
  getContextualQuickReplies,
} from "./flows/flow.areas.js";
import { evaluateScope } from "@tennis-matches/core/ai/scopePolicy";
import {
  fetchAiThreadMessages,
  fetchOrCreateAiThread,
} from "../../utils/aiChatApi.js";
import {
  resolveAiChatErrorKey,
  sendAiChatMessageRemote,
} from "@tennis-matches/services/ai-chat";
import {
  buildAssistantFromChatResponse,
  serializeChatMessagesForRemote,
} from "./buildAssistantFromChatResponse.js";
import { GENERIC_HELP } from "./intentKeywords.js";
import {
  X,
  Send,
  Sparkles,
  RotateCcw,
  Crosshair,
  Swords,
  Clock,
  Trophy,
  Settings2,
} from "lucide-react";
import {
  getSelectableModePresets,
  SCHLEIFCHEN_SCORING_OPTIONS,
  MODE_ICON_NAMES,
} from "@tennis-matches/ui";

/**
 * @typedef {{ id: string; labelKey: string; action: "send"; payload?: unknown }} Suggestion
 * @typedef {{ id: string; labelKey: string; value: string }} QuickReplyItem
 * @typedef {{ id: string; role: "user" | "assistant"; content: string; toolCalls?: Array<{ name: string; args: Record<string, unknown> }>; createdSession?: { id: string }; suggestions?: Suggestion[]; quickReplies?: QuickReplyItem[]; kind?: "normal" | "flow" | "training" | "coachdesk-ask"; flowChoiceValues?: string[]; pendingTrainingConfirm?: { personId: string; playerName: string }; ambiguousPeople?: Array<{ id?: string; name: string }> }} Message
 */

const CHAT_WIZARD_ICONS = { Crosshair, Swords, Clock, Trophy, Settings2 };

/** chatMode: "general" = freies Gespräch (Quick Replies), "flow" = strukturierter Wizard/Flow – keine Zufalls-Vorschläge */
function getChatMode(chatWizard) {
  return chatWizard ? "flow" : "general";
}

function isGenericUserText(text) {
  if (!text || typeof text !== "string") return true;
  const t = text.trim().toLowerCase();
  if (t.length < 40) return true;
  return GENERIC_HELP.some((kw) => t.includes(kw));
}

/**
 * Deterministische Vorschläge aus PageContext/Area – keine LLM-generierten.
 * Nur wenn KEIN aktiver Flow (chatFlowState.active === false) und general-Modus.
 */
function buildSuggestions({ chatMode, pageContext, lastUserText, flowActive }) {
  if (flowActive || chatMode !== "general" || !pageContext) return [];
  if (
    lastUserText !== undefined &&
    lastUserText !== null &&
    !isGenericUserText(String(lastUserText))
  ) {
    return [];
  }
  if (
    pageContext.page === "ki-assistant" &&
    AREA_SUGGESTION_ITEMS["ki-assistant"]
  ) {
    return AREA_SUGGESTION_ITEMS["ki-assistant"].slice(0, 4).map((item, i) => ({
      id: item.id || `sug-ki-${i}`,
      labelKey: item.labelKey,
      action: item.action || "send",
      ...(item.route ? { route: item.route } : {}),
    }));
  }
  const area = String(pageContext.area || "home");
  const fixed = AREA_SUGGESTION_ITEMS[area];
  if (fixed) return fixed.slice(0, 6);
  const base = [];
  if (pageContext.suggestionKeys && Array.isArray(pageContext.suggestionKeys)) {
    pageContext.suggestionKeys.forEach((key, i) => {
      base.push({ id: `sug-${area}-${i}`, labelKey: key, action: "send" });
    });
  }
  return base.slice(0, 6);
}

/** Entfernt nummerierte Listen im Fließtext, wenn UI-Chips angezeigt werden (keine doppelte Optionsliste). */
function sanitizeAssistantTextWhenChips(content) {
  if (!content || typeof content !== "string") return content;
  const lines = content.split("\n");
  const out = [];
  let inNumberedList = false;
  for (const line of lines) {
    const trimmed = line.trim();
    const numberedMatch = /^\d+[.)]\s+.+/.test(trimmed);
    if (numberedMatch) {
      inNumberedList = true;
      continue;
    }
    if (inNumberedList && (trimmed === "" || /^[-*]\s+/.test(trimmed)))
      continue;
    inNumberedList = false;
    out.push(line);
  }
  return out
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function chatWizardReducer(state, action) {
  switch (action.type) {
    case "start":
      return { step: "mode", draft: {} };
    case "setAndNext":
      return {
        step: action.payload.nextStep,
        draft: { ...state.draft, [action.payload.field]: action.payload.value },
      };
    case "reset":
      return null;
    default:
      return state;
  }
}

function ChatWizardCards({
  step,
  draft,
  t,
  onSelect,
  onNavigateToWizard,
  onCancel,
}) {
  const modePresets = useMemo(() => getSelectableModePresets(), []);
  const scoringOptions = SCHLEIFCHEN_SCORING_OPTIONS;

  if (step === "mode") {
    return (
      <div className="ai-panel__wizard-cards">
        <p className="ai-panel__wizard-label">{t("settings.rr.mode")}</p>
        <div className="ai-panel__wizard-cards-grid">
          {modePresets.map((preset) => {
            const IconName = MODE_ICON_NAMES[preset.id];
            const Icon = (IconName && CHAT_WIZARD_ICONS[IconName]) || Settings2;
            const isSelected = draft.mode === preset.id;
            return (
              <button
                key={preset.id}
                type="button"
                className={`ai-panel__wizard-card ${isSelected ? "ai-panel__wizard-card--selected" : ""}`}
                onClick={() => onSelect("mode", preset.id, preset.labelKey)}
              >
                <span className="ai-panel__wizard-card-icon">
                  <Icon className="ai-panel__wizard-icon" aria-hidden />
                </span>
                <span className="ai-panel__wizard-card-title">
                  {t(preset.labelKey)}
                </span>
                <span className="ai-panel__wizard-card-desc">
                  {t(preset.descKey)}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  if (step === "scoring") {
    return (
      <div className="ai-panel__wizard-cards">
        <p className="ai-panel__wizard-label">{t("settings.rr.mode")}</p>
        <div className="ai-panel__wizard-cards-grid ai-panel__wizard-cards-grid--compact">
          {scoringOptions.map((opt) => {
            const isSelected = draft.scoring === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                className={`ai-panel__wizard-card ai-panel__wizard-card--compact ${isSelected ? "ai-panel__wizard-card--selected" : ""}`}
                onClick={() => onSelect("scoring", opt.value, opt.key)}
              >
                <span className="ai-panel__wizard-card-title">
                  {t(opt.key)}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  if (step === "summary") {
    const modePreset = modePresets.find((p) => p.id === draft.mode);
    const scoringOpt = scoringOptions.find((o) => o.value === draft.scoring);
    return (
      <div className="ai-panel__wizard-summary">
        <p className="ai-panel__wizard-label">
          {t("ai.chatWizard.summary.title")}
        </p>
        <ul className="ai-panel__wizard-summary-list">
          <li>
            <span className="ai-panel__wizard-summary-key">
              {t("settings.wizard.summary.mode")}
            </span>
            <span className="ai-panel__wizard-summary-val">
              {modePreset ? t(modePreset.labelKey) : "—"}
            </span>
          </li>
          <li>
            <span className="ai-panel__wizard-summary-key">
              {t("settings.rr.mode")}
            </span>
            <span className="ai-panel__wizard-summary-val">
              {scoringOpt ? t(scoringOpt.key) : "—"}
            </span>
          </li>
        </ul>
        <div className="ai-panel__wizard-buttons">
          <button
            type="button"
            className="btn btn--primary btn--sm"
            onClick={onNavigateToWizard}
          >
            {t("ai.chatWizard.toWizard")}
          </button>
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={onCancel}
          >
            {t("ai.chatWizard.cancel")}
          </button>
        </div>
      </div>
    );
  }

  return null;
}

/**
 * @param {{ layout?: "drawer" | "page" }} props
 */
export function AIChatPanel({ layout = "drawer" }) {
  const pageLayout = layout === "page";
  const { isOpen, setIsOpen, pageContext, t, userDisplayName } = useAI();
  const navigate = useNavigate();
  const lastNavigationRef = useRef(
    /** @type {{ path: string; ts: number } | null} */ (null),
  );
  const runner = useMemo(
    () => createToolRunner({ navigate, lastNavigationRef }),
    [navigate],
  );
  const [messages, setMessages] = useState(/** @type {Message[]} */ ([]));
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [threadId, setThreadId] = useState("");
  const [executedToolCallIds, setExecutedToolCallIds] = useState(
    /** @type {Set<string>} */ (() => new Set()),
  );
  const messagesEndRef = useRef(/** @type {HTMLDivElement | null} */ (null));
  const inputRef = useRef(/** @type {HTMLInputElement | null} */ (null));

  /** Zentraler Flow-State (chatFlowState): active, flowId, stepId, answers – bei active gehört die Konversation dem Wizard */
  const [flowState, setFlowState] = useState(getInitialState);
  const [chatWizard, dispatchChatWizard] = useReducer(chatWizardReducer, null);
  const chatMode = getChatMode(chatWizard);
  const hasActiveFlow = flowState.active;
  const isHandlingPickRef = useRef(false);
  const lastAssistantMessageId = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === "assistant") return messages[i].id;
    }
    return null;
  }, [messages]);
  const lastUserText = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === "user") return messages[i].content;
    }
    return "";
  }, [messages]);

  const hasRestoredRef = useRef(false);
  useEffect(() => {
    if (!isOpen && !pageLayout) return;
    if (hasRestoredRef.current) return;
    hasRestoredRef.current = true;
    let cancelled = false;
    (async () => {
      const thread = await fetchOrCreateAiThread();
      if (cancelled) return;
      if (thread?.id) setThreadId(thread.id);
      const dbMsgs = thread?.id ? await fetchAiThreadMessages(thread.id) : [];
      if (cancelled) return;
      if (dbMsgs.length > 0) {
        setMessages(
          dbMsgs.map((m) => ({
            id: crypto.randomUUID(),
            role: m.role === "assistant" ? "assistant" : "user",
            content: m.content,
          })),
        );
      } else {
        const stored = loadStoredMessages();
        if (stored.length > 0) setMessages(stored);
      }
      const storedFlow = loadFlowState();
      if (storedFlow && storedFlow.active) setFlowState(storedFlow);
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, pageLayout]);

  useEffect(() => {
    saveStoredMessages(messages);
  }, [messages]);
  useEffect(() => {
    saveFlowState(flowState.active ? flowState : null);
  }, [flowState]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (isOpen || pageLayout) {
      const id = setTimeout(() => inputRef.current?.focus(), 300);
      return () => clearTimeout(id);
    }
  }, [isOpen, pageLayout]);

  // Run tool calls once per assistant message (after render)
  useEffect(() => {
    const toRun = [];
    for (const msg of messages) {
      if (
        msg.role !== "assistant" ||
        !Array.isArray(msg.toolCalls) ||
        msg.toolCalls.length === 0 ||
        executedToolCallIds.has(msg.id)
      )
        continue;
      toRun.push(msg);
    }
    if (toRun.length === 0) return;
    const ids = toRun.map((m) => m.id);
    setExecutedToolCallIds((prev) => new Set([...prev, ...ids]));
    setTimeout(() => {
      for (const msg of toRun) runner.runToolCalls(msg.toolCalls);
    }, 0);
  }, [messages, runner, executedToolCallIds]);

  const sendMessage = async (content, opts = {}) => {
    const displayContent =
      opts.displayContent != null ? String(opts.displayContent) : null;
    const raw = String(content ?? "").trim();
    if (!raw && !displayContent) return;
    if (isLoading) return;
    const userContent = (
      displayContent != null && displayContent !== "" ? displayContent : raw
    ).trim();
    const valueForFlow = raw || userContent;
    const userMessage = {
      id: crypto.randomUUID(),
      role: "user",
      content: userContent,
    };
    setMessages((prev) => [...prev, userMessage]);

    // Regel: Bei aktivem Flow keine Intent-Erkennung, keine neuen Vorschläge – jede Aktion (Text/Klick) an den Wizard
    if (hasActiveFlow) {
      const result = handleUserMessage(flowState, valueForFlow);
      setFlowState(result.state);
      if (result.action === "cancel") return;
      if (result.action === "assistant" && result.assistantTextKey) {
        const assistantMsg = {
          id: crypto.randomUUID(),
          role: "assistant",
          content: t(result.assistantTextKey, result.assistantTextParams),
          quickReplies: result.quickReplies,
          kind: "flow",
          toolCalls: result.toolCalls,
        };
        setMessages((prev) => [...prev, assistantMsg]);
      }
      return;
    }

    // Intent-Routing nur wenn active === false
    const lastMsg = messages[messages.length - 1];
    const isChooseModuleReply =
      lastMsg?.role === "assistant" &&
      Array.isArray(lastMsg.flowChoiceValues) &&
      lastMsg.flowChoiceValues.includes(valueForFlow.toLowerCase());
    if (isChooseModuleReply) {
      const flowId = getFlowIdForArea(valueForFlow.toLowerCase());
      if (!flowId) return;
      const out = startFlow(flowState, flowId);
      setFlowState(out.state);
      if (out.assistantTextKey) {
        setMessages((prev) => [
          ...prev,
          {
            id: crypto.randomUUID(),
            role: "assistant",
            content: t(out.assistantTextKey, out.assistantTextParams),
            quickReplies: out.quickReplies,
            kind: "flow",
            toolCalls: out.toolCalls,
          },
        ]);
      }
      return;
    }

    const openRoute = detectOpenAppRoute(valueForFlow);
    if (openRoute) {
      navigateTo(openRoute, true);
      setIsOpen(false);
      return;
    }

    const intent = detectIntent(valueForFlow);
    if (intent === "plan_tournament") {
      const route = routeIntent(intent, pageContext);
      if (route.startFlowId) {
        const out = startFlow(flowState, route.startFlowId);
        setFlowState(out.state);
        if (out.assistantTextKey) {
          setMessages((prev) => [
            ...prev,
            {
              id: crypto.randomUUID(),
              role: "assistant",
              content: t(out.assistantTextKey, out.assistantTextParams),
              quickReplies: out.quickReplies,
              kind: "flow",
              toolCalls: out.toolCalls,
            },
          ]);
        }
        return;
      }
      if (route.assistantTextKey && route.quickReplies) {
        setMessages((prev) => [
          ...prev,
          {
            id: crypto.randomUUID(),
            role: "assistant",
            content: t(route.assistantTextKey),
            quickReplies: route.quickReplies,
            kind: "flow",
            flowChoiceValues: [...TOURNAMENT_AREAS],
          },
        ]);
        return;
      }
    }

    // Off-topic guard: no LLM call for clearly out-of-scope questions
    const scopeResult = evaluateScope(raw);
    if (!scopeResult.allowed) {
      setMessages((prev) => [
        ...prev,
        {
          id: crypto.randomUUID(),
          role: "assistant",
          content: t("ai.scope.offTopic.body"),
          quickReplies: [...SCOPE_OFF_TOPIC_QUICK_REPLIES],
          kind: "flow",
        },
      ]);
      return;
    }

    setIsLoading(true);

    const allMessages = [...messages, userMessage];
    const todayYmd = new Date().toISOString().slice(0, 10);

    try {
      const { ok, status, data } = await sendAiChatMessageRemote({
        messages: serializeChatMessagesForRemote(allMessages),
        ...(threadId ? { threadId } : {}),
        pageContext: {
          area: pageContext.area,
          page: pageContext.page,
          labelKey: pageContext.labelKey,
          todayYmd,
          ...(userDisplayName ? { userDisplayName } : {}),
        },
      });

      if (ok && data?.message) {
        setMessages((prev) => [
          ...prev,
          buildAssistantFromChatResponse(data, {
            t,
            pageContext,
            lastUserText: userContent,
            buildSuggestions,
            sanitizeAssistantTextWhenChips,
          }),
        ]);
        return;
      }

      const errorKey = resolveAiChatErrorKey(status, data);
      setMessages((prev) => [
        ...prev,
        { id: crypto.randomUUID(), role: "assistant", content: t(errorKey) },
      ]);
    } catch {
      setMessages((prev) => [
        ...prev,
        {
          id: crypto.randomUUID(),
          role: "assistant",
          content: t(resolveAiChatErrorKey(0, {})),
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSend = () => {
    if (!input.trim() || isLoading) return;
    const text = input.trim();
    setInput("");
    sendMessage(text);
  };

  /** Klick auf Vorschlag im Empty-State – navigate oder send je nach action */
  const handleSuggestion = (suggestion) => {
    handleQuickReplyClick(
      typeof suggestion === "string"
        ? { id: "legacy-key", labelKey: suggestion, action: "send" }
        : suggestion,
    );
  };

  /** Wizard-Chip-Klick = sofort nächste Wizard-Antwort (kein „Weiter“, keine Intent-Auswertung); gleichwertig zu Texteingabe */
  const handleQuickReplyClick = (suggestion) => {
    if (suggestion.action === "navigate" && suggestion.route) {
      navigateTo(suggestion.route, true);
      setIsOpen(false);
      return;
    }
    if (suggestion.value != null) {
      const display =
        suggestion.label ||
        (suggestion.labelKey
          ? t(suggestion.labelKey)
          : String(suggestion.value));
      sendMessage(suggestion.value, { displayContent: display });
    } else if (suggestion.action === "send") {
      sendMessage(t(suggestion.labelKey));
    }
  };

  const handleReset = () => {
    setMessages([]);
    setInput("");
    setExecutedToolCallIds(new Set());
    setFlowState(getInitialState());
    saveFlowState(null);
    dispatchChatWizard({ type: "reset" });
  };

  const handleChatWizardSelect = (field, value, displayLabelKey) => {
    if (isHandlingPickRef.current) return;
    isHandlingPickRef.current = true;
    const label = displayLabelKey ? t(displayLabelKey) : String(value);
    setMessages((prev) => [
      ...prev,
      { id: crypto.randomUUID(), role: "user", content: label },
    ]);
    if (chatWizard.step === "mode") {
      dispatchChatWizard({
        type: "setAndNext",
        payload: { field, value, nextStep: "scoring" },
      });
    } else if (chatWizard.step === "scoring") {
      dispatchChatWizard({
        type: "setAndNext",
        payload: { field, value, nextStep: "summary" },
      });
    }
    requestAnimationFrame(() => {
      isHandlingPickRef.current = false;
    });
  };

  const handleNavigateToWizard = () => {
    const prefix = AREA_UI[pageContext.area]?.setupRoutePrefix;
    if (!prefix) return;
    const rrMode =
      chatWizard?.draft?.scoring ||
      (() => {
        const preset = getSelectableModePresets().find(
          (p) => p.id === chatWizard?.draft?.mode,
        );
        return preset?.rrModeValue || "ONE_SET_OPEN";
      })();
    setIsOpen(false);
    navigateTo(
      `${prefix}?new=1&prefillRrMode=${encodeURIComponent(rrMode)}`,
      true,
    );
  };

  const panelOpen = pageLayout || isOpen ? "ai-panel--open" : "";
  const panelClassName = pageLayout
    ? `ai-panel ai-panel--page ${panelOpen}`
    : `ai-panel ${panelOpen}`;

  if (!pageLayout && !isOpen) return null;

  return (
    <>
      {!pageLayout && isOpen ? (
        <div
          className="ai-panel__backdrop"
          onClick={() => setIsOpen(false)}
          aria-hidden="true"
        />
      ) : null}

      <div className={panelClassName}>
        <div className="ai-panel__header">
          <div className="ai-panel__header-title">
            <div className="ai-panel__icon-wrap">
              <Sparkles className="ai-panel__icon" aria-hidden />
            </div>
            <div>
              <h3 className="ai-panel__heading">{t("ai.panel.title")}</h3>
            </div>
          </div>
          <div className="ai-panel__header-actions">
            {(messages.length > 0 || chatWizard || hasActiveFlow) && (
              <button
                type="button"
                className="btn btn--ghost btn--icon ai-panel__btn-icon"
                onClick={handleReset}
                title={t("ai.reset")}
                aria-label={t("ai.reset")}
              >
                <RotateCcw className="ai-panel__icon-sm" aria-hidden />
              </button>
            )}
            {!pageLayout ? (
              <button
                type="button"
                className="btn btn--ghost btn--icon ai-panel__btn-icon"
                onClick={() => setIsOpen(false)}
                aria-label={t("ai.fab.close")}
              >
                <X className="ai-panel__icon-sm" aria-hidden />
              </button>
            ) : null}
          </div>
        </div>

        <div className="ai-panel__messages">
          {messages.length === 0 && !chatWizard && !hasActiveFlow ? (
            <div className="ai-panel__empty">
              <div className="ai-panel__empty-icon-wrap">
                <Sparkles className="ai-panel__empty-icon" aria-hidden />
              </div>
              <div className="ai-panel__empty-text">
                <h3 className="ai-panel__empty-title">{t("ai.empty.title")}</h3>
                <p className="ai-panel__empty-subtitle">
                  {userDisplayName
                    ? t("ai.empty.subtitleWithName", {
                        name: userDisplayName,
                      })
                    : t("ai.empty.subtitle")}
                </p>
              </div>
              <div className="ai-panel__suggestions">
                {getEmptyStateSuggestions(pageContext).map((sug) => (
                  <button
                    key={sug.id}
                    type="button"
                    className="ai-panel__suggestion-btn"
                    onClick={() => handleSuggestion(sug)}
                  >
                    {t(sug.labelKey)}
                  </button>
                ))}
                {AREA_UI[pageContext.area]?.hasWizardButton && (
                  <button
                    type="button"
                    className="ai-panel__suggestion-btn ai-panel__suggestion-btn--wizard"
                    onClick={() => dispatchChatWizard({ type: "start" })}
                  >
                    {t("ai.chatWizard.start")}
                  </button>
                )}
              </div>
            </div>
          ) : (
            <>
              {messages.map((msg) => {
                const isLastAssistant =
                  msg.role === "assistant" && msg.id === lastAssistantMessageId;
                const showQuickReplies =
                  isLastAssistant &&
                  !isLoading &&
                  (hasActiveFlow
                    ? Array.isArray(msg.quickReplies) &&
                      msg.quickReplies.length > 0
                    : true);
                const quickReplyList = !showQuickReplies
                  ? []
                  : Array.isArray(msg.quickReplies) &&
                      msg.quickReplies.length > 0
                    ? msg.quickReplies
                    : getContextualQuickReplies(msg).length > 0
                      ? getContextualQuickReplies(msg)
                      : !hasActiveFlow &&
                          chatMode === "general" &&
                          !chatWizard &&
                          !msg.toolCalls?.length &&
                          !msg.pendingTrainingConfirm
                        ? Array.isArray(msg.suggestions) &&
                          msg.suggestions.length > 0
                          ? msg.suggestions
                          : buildSuggestions({
                              chatMode,
                              pageContext,
                              lastUserText,
                              flowActive: hasActiveFlow,
                            })
                        : [];

                return (
                  <div
                    key={msg.id}
                    className={
                      msg.role === "user"
                        ? "ai-panel__msg ai-panel__msg--user"
                        : "ai-panel__msg ai-panel__msg--assistant"
                    }
                  >
                    <div className="ai-panel__bubble-wrap">
                      <div
                        className={
                          msg.role === "user"
                            ? "ai-panel__bubble ai-panel__bubble--user"
                            : "ai-panel__bubble ai-panel__bubble--assistant"
                        }
                      >
                        {msg.content}
                      </div>
                      {msg.role === "assistant" &&
                        msg.createdSession &&
                        msg.createdSession.id && (
                          <div className="ai-panel__suggested-actions">
                            <button
                              type="button"
                              className="btn btn--primary btn--sm"
                              onClick={() => {
                                const sid = String(
                                  msg.createdSession.id || "",
                                ).trim();
                                if (!sid) return;
                                setIsOpen(false);
                                navigateTo(
                                  `${buildTrainingsDeskPath("planung")}?session=${encodeURIComponent(sid)}`,
                                  true,
                                );
                              }}
                            >
                              {t("ai.training.viewSession")}
                            </button>
                          </div>
                        )}
                      {quickReplyList.length > 0 && (
                        <div className="ai-panel__quick-replies">
                          {quickReplyList.map((sug) => (
                            <button
                              key={sug.id}
                              type="button"
                              className="ai-panel__quick-reply-chip"
                              onClick={() => handleQuickReplyClick(sug)}
                            >
                              {sug.label ||
                                (sug.labelKey
                                  ? t(sug.labelKey)
                                  : sug.value || "")}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
              {chatWizard && !hasActiveFlow && (
                <div className="ai-panel__msg ai-panel__msg--assistant">
                  <div className="ai-panel__bubble-wrap">
                    <div className="ai-panel__bubble ai-panel__bubble--assistant">
                      <ChatWizardCards
                        step={chatWizard.step}
                        draft={chatWizard.draft}
                        t={t}
                        onSelect={handleChatWizardSelect}
                        onNavigateToWizard={handleNavigateToWizard}
                        onCancel={() => dispatchChatWizard({ type: "reset" })}
                      />
                    </div>
                  </div>
                </div>
              )}
              {isLoading && (
                <div className="ai-panel__msg ai-panel__msg--assistant">
                  <div className="ai-panel__bubble ai-panel__bubble--typing">
                    <span className="ai-panel__dot" aria-hidden />
                    <span className="ai-panel__dot" aria-hidden />
                    <span className="ai-panel__dot" aria-hidden />
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        <div className="ai-panel__input-wrap">
          <form
            className="ai-panel__form"
            onSubmit={(e) => {
              e.preventDefault();
              handleSend();
            }}
          >
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={t("ai.input.placeholder")}
              className="ai-panel__input"
              disabled={isLoading}
              aria-label={t("ai.input.placeholder")}
            />
            <button
              type="submit"
              className="btn btn--primary btn--icon ai-panel__send"
              disabled={!input.trim() || isLoading}
              aria-label={t("ai.send")}
            >
              <Send className="ai-panel__icon-sm" aria-hidden />
            </button>
          </form>
        </div>
      </div>
    </>
  );
}
